/*
 * Copyright (c) 2018 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import _ from 'lodash';
import can from 'can';
import Component from 'can/component/component';
import template from './history-player-statistics.stache';
import appContext from 'scripts/app-context';
import { updateComparator } from '../utils';
import dtm from 'scripts/services/dtm';

import YearFallbackFeedFacade from '../year-fallback-feed-facade';
import dayjs from 'dayjs';

const playerPageUrl = window.globalSettings.pagePaths.player;
const scoringLeaderboardUrl = window.globalSettings.feeds.history.scoring.stats;
const subscriptionKey = window.globalSettings.feeds.history.subscriptionKey;

const MAP_FEEDS = {
    'driving-distance': 'drive',
    'fairways-hit': 'fairwayhit',
    'greens-in-regulation': 'gir',
    putting: 'putting',
    birdies: 'birdies',
    eagles: 'eagles',
};

const simpleKpis = ['driving-distance', 'putting', 'birdies', 'eagles'];

const formatValue = (value, kpi) => {
    if ([simpleKpis[0], simpleKpis[1]].includes(kpi)) {
        return value.toFixed(2);
    }

    return value.toFixed(0);
};

Component.extend({
    tag: 'history-player-statistics',
    template: template,
    leakScope: false,
    viewModel: {
        define: {
            fetching: {
                type: 'boolean',
                value: true,
            },
            kpi: {
                type: 'string',
                value: 'fairways-hit',
            },
            isKpiSimple: {
                type: 'boolean',
                get() {
                    return Boolean(~simpleKpis.indexOf(this.attr('kpi')));
                },
            },
            settings: {
                type: 'object',
            },
            sortKey: {
                type: 'string',
                value: '',
            },
            sortAsc: {
                type: 'boolean',
                value: true,
            },
            players: {
                Type: can.List,
                Value: can.List,
            },
            yearSelected: {
                type: 'number',
                value: 2018, // todo make this automated
            },
            dataNotAvailable: {
                type: 'boolean',
                value: false,
            },
        },

        isEmptyStats(stats) {
            return !stats || !stats.value;
        },

        getDisplayEntry(stats) {
            const isSimpleKpi = this.attr('isKpiSimple');

            const value = formatValue(stats.value, this.attr('kpi'));

            if (isSimpleKpi) {
                return value;
            }

            return `${value} / ${stats.max}`;
        },

        getPercentageEntry(stats) {
            return `( ${stats.relative}%)`;
        },
        createSortingStatField(round) {
            return `roundStats.${round}.value`;
        },

        getSortingField(sortKey) {
            switch (sortKey) {
                case 'rank':
                    return 'rank';
                case 'pos':
                    return 'pos';
                case 'player':
                    return 'lastName';
                case '1':
                    return this.createSortingStatField('0');
                case '2':
                    return this.createSortingStatField('1');
                case '3':
                    return this.createSortingStatField('2');
                case '4':
                    return this.createSortingStatField('3');
                default:
                    return 'rank';
            }
        },

        playerDetailLink(playerId) {
            return can.sub(playerPageUrl, { playerId });
        },

        getLegend(currentYear) {
            const legendArr = this.attr('legend') || [];
            const legend = _.find(
                legendArr,
                (it) => it.year === parseInt(this.attr('yearSelected')),
            );
            let result = [];

            if (_.get(legend, 'location')) {
                result.push(legend.location);
            }

            if (_.get(legend, 'start')) {
                let dateResult = dayjs(legend.start).format('MMMM DD');

                if (_.get(legend, 'end')) {
                    const endMomentDate = dayjs(legend.end);
                    dateResult +=
                        ' - ' +
                        endMomentDate.format(
                            endMomentDate.isSame(dayjs(legend.start), 'month')
                                ? 'DD'
                                : 'MMMM DD',
                        );
                }

                result.push(dateResult);
            }

            const years = this.attr('settings.legendYears');
            if (
                years &&
                currentYear &&
                years.indexOf(currentYear.toString()) != -1
            ) {
                result.push(this.attr('settings.legendYearText'));
            }

            return result.join(', ');
        },
    },

    events: {
        inserted() {
            this.registerFeedService();
            setTimeout(() => {
                this.fetchData();
            }, 100);

            appContext.model.bind(
                'history-experience-scoring-and-stats-years-change',
                this.handleYearChange.bind(this),
            );
            appContext.model.bind(
                'history-experience-stats-kpi-change',
                this.handleKpiChange.bind(this),
            );
            appContext.model.bind(
                'history-experience-scoring-and-stats-years-ready',
                (_, years, legend) => {
                    this.viewModel.attr('legend', legend);
                },
            );
            this.viewModel.bind('sortKey', () => {
                updateComparator(this.viewModel, 'players');
            });
            this.viewModel.bind('sortAsc', () => {
                updateComparator(this.viewModel, 'players');
            });

            appContext.model.dispatch('history-experience-stats-ready');
        },

        registerFeedService() {
            const headers = subscriptionKey
                ? { ['Ocp-Apim-Subscription-Key']: subscriptionKey }
                : {};

            this.feedService = new YearFallbackFeedFacade({
                feedUrl: scoringLeaderboardUrl,
                fromCache: false,
                autoUpdate: false,
                headers,
            })
                .onData((_, data) => {
                    can.batch.start();

                    this.viewModel.attr('fetching', false);
                    this.viewModel.attr('dataNotAvailable', false);
                    this.viewModel.attr('players', data.players);
                    updateComparator(this.viewModel, 'players');
                    can.batch.stop();
                })
                .onError((_, error) => {
                    console.warn(`Error - ${error}`);
                    this.viewModel.attr('dataNotAvailable', true);
                    this.viewModel.attr('fetching', false);
                });
        },

        fetchData(identifier = 'meta') {
            const kpi = this.viewModel.attr('kpi');
            if (!kpi) {
                return false;
            }

            const point = MAP_FEEDS[kpi];

            this.viewModel.attr('fetching', true);
            this.feedService.fetch(identifier, { year: identifier, point });
        },

        '.js-sorting-field click'(element, event) {
            const sortKey = element.attr('data-sort-key');
            can.batch.start();
            if (this.viewModel.attr('sortKey') === sortKey) {
                this.viewModel.attr('sortAsc', !this.viewModel.attr('sortAsc'));
            } else {
                this.viewModel.attr('sortKey', sortKey);
                this.viewModel.attr('sortAsc', true);
            }

            can.batch.stop();
            dtm.trackRoundTrackerClick(sortKey);
        },
        handleYearChange(_, year) {
            if (!Boolean(year)) {
                return;
            }

            this.viewModel.attr('yearSelected', year);
            this.fetchData(year);
        },

        handleKpiChange(_, kpi) {
            this.viewModel.attr('kpi', kpi);
            this.fetchData(this.viewModel.attr('yearSelected'));
        },
    },
});
